/** t331u B12: phone Settings › Kudos badges and › Celebrations (desktop KudosBadgesAdmin and EngagementPanel). Same API and checks. */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Archive, ArrowDown, ArrowUp } from 'lucide-react';
import { BADGE_EMOJI_IDEAS, BADGE_PALETTE, badgeBody, badgeForm, badgeGroups, badgeInvalid, badgeLine, badgeOrder, badgeProblem, BADGES_NOTE, BIRTHDAY_CHANNEL_HINT, birthdayChannels, CELEBRATIONS_NOTE, type BadgeForm, type CelebrationsForm, type KudosBadgeDef } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Input, Select, Spinner, ask } from '@/components/ui';
import { badgesKey } from '@/features/people/badges';
import { prefsKey, useWorkspacePrefs } from '@/features/settings/PrefsPanels';
import { BottomSheet } from '../BottomSheet';
import { card, F, ghostBtn, saveBtn } from './PersonSettings';
import { ColourSwatches, listCard, note, WsAddRow, WsHead, WsRow, WsSwitch } from './WorkspaceSettings';
import { SaveStatus, useAutosaveForm } from '@/components/ui/autosave';

export function KudosBadgesPage() {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const q = useQuery({ queryKey: badgesKey(ws ?? ''), queryFn: () => api.kudosBadges(ws!), enabled: Boolean(ws), staleTime: 5 * 60_000 });
  const [open, setOpen] = useState<{ b: KudosBadgeDef | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const all = q.data?.badges ?? [];
  const g = badgeGroups(all);
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try { await fn(); toast(ok, 'success'); void queryClient.invalidateQueries({ queryKey: badgesKey(ws ?? '') }); return true; }
    catch (e) { toast(errorMessage(e, 'Couldn’t save the badge'), 'error'); return false; }
    finally { setBusy(false); }
  };
  const move = (id: string, by: -1 | 1) => void run(async () => { const r = await api.reorderKudosBadges(ws!, badgeOrder(all, id, by)); queryClient.setQueryData(badgesKey(ws ?? ''), (o: typeof q.data) => (o ? { ...o, badges: r.badges } : o)); }, 'Order saved');
  const arrow = 'flex h-11 w-11 items-center justify-center text-slate-600 disabled:opacity-30 dark:text-slate-300';
  const row = (b: KudosBadgeDef, i?: number) => (
    <WsRow key={b.id} testId="m-badges-row" color={b.color} title={`${b.emoji} ${b.name}`} line={badgeLine(b)} onClick={() => setOpen({ b })}
      right={i !== undefined && g.live.length > 1 ? <span className="flex shrink-0 pr-1">
        <button type="button" className={arrow} aria-label={`Move ${b.name} up`} disabled={busy || i === 0} onClick={() => move(b.id, -1)}><ArrowUp className="h-5 w-5" /></button>
        <button type="button" className={arrow} aria-label={`Move ${b.name} down`} disabled={busy || i === g.live.length - 1} onClick={() => move(b.id, 1)}><ArrowDown className="h-5 w-5" /></button>
      </span> : undefined} />
  );
  return (
    <div className="min-h-0 flex-1 overflow-y-auto pb-4" data-testid="m-badges">
      <p className={note}>{BADGES_NOTE}</p>
      <div className={`${listCard} mt-3`} data-testid="m-badges-list">
        {q.isLoading ? <div className="p-4"><Spinner /></div> : !g.live.length && <p className="border-b border-slate-100 px-3 py-3 text-sm text-slate-500 dark:border-slate-800">No badges yet.</p>}
        {g.live.map((b, i) => row(b, i))}
        <WsAddRow label="Add a badge" onClick={() => setOpen({ b: null })} testId="m-badges-add" />
      </div>
      {g.retired.length > 0 && <><WsHead>Retired</WsHead><div className={listCard} data-testid="m-badges-retired">{g.retired.map(b => row(b))}</div></>}
      {g.system.length > 0 && <><WsHead>Automatic</WsHead><div className={listCard} data-testid="m-badges-system">{g.system.map(b => row(b))}</div><p className={note}>Posted automatically for birthdays and sales targets.</p></>}
      {open && <BadgeSheet b={open.b} busy={busy} onClose={() => setOpen(null)}
        onSave={f => run(() => (open.b ? api.updateKudosBadge(ws!, open.b.id, badgeBody(f)) : api.createKudosBadge(ws!, badgeBody(f))), open.b ? 'Badge saved' : 'Badge added')}
        onRetire={open.b && !open.b.system ? async () => {
          const b = open.b!;
          if (b.retired_at) return run(() => api.updateKudosBadge(ws!, b.id, { retired: false }), `${b.name} restored`);
          return (await ask({ title: `Retire “${b.name}”?`, body: 'It stays on existing kudos but can’t be picked any more.', confirmLabel: 'Retire' })) ? run(() => api.updateKudosBadge(ws!, b.id, { retired: true }), `${b.name} retired`) : false;
        } : undefined} />}
    </div>
  );
}

function BadgeSheet({ b, busy, onClose, onSave, onRetire }: { b: KudosBadgeDef | null; busy: boolean; onClose: () => void; onSave: (f: BadgeForm) => Promise<boolean>; onRetire?: () => Promise<boolean> }) {
  const [f, setF] = useState<BadgeForm>(() => badgeForm(b));
  const problem = badgeProblem(f);
  return (
    <BottomSheet open onClose={onClose} title={b ? `Edit ${b.name}` : 'Add a badge'} testId="m-badges-sheet"
      footer={<div className="flex gap-2 px-3 py-2">
        {onRetire && <button type="button" className={ghostBtn} disabled={busy} onClick={async () => { if (await onRetire()) onClose(); }} data-testid="m-badges-retire">
          <Archive className="h-5 w-5" />{b?.retired_at ? 'Restore' : 'Retire'}</button>}
        <button type="button" className={saveBtn} disabled={busy || badgeInvalid(f)} onClick={async () => { if (await onSave(f)) onClose(); }} data-testid="m-badges-save">{b ? 'Save' : 'Add badge'}</button>
      </div>}>
      <div className="space-y-1 px-4 pb-2">
        <F label="Name"><Input placeholder="e.g. Team player" maxLength={40} value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="m-badges-name" /></F>
        <F label="Emoji">
          <div className="flex flex-wrap items-center gap-1" data-testid="m-badges-emoji">
            <Input className="!w-16 text-center text-lg" aria-label="Emoji" value={f.emoji} onChange={e => setF({ ...f, emoji: e.target.value })} />
            {BADGE_EMOJI_IDEAS.map(e => <button key={e} type="button" aria-label={`Use ${e}`} onClick={() => setF({ ...f, emoji: e })} className={clsx('flex h-11 w-11 items-center justify-center rounded-lg text-xl', f.emoji === e && 'ring-2 ring-black dark:ring-white')}>{e}</button>)}
          </div>
        </F>
        <div className="py-1.5"><span className="mb-1 block text-xs font-semibold uppercase text-slate-600 dark:text-slate-400">Colour</span><ColourSwatches palette={BADGE_PALETTE} value={f.color} onChange={color => setF({ ...f, color })} testId="m-badges-colour" /></div>
        <F label="Description (optional)"><Input maxLength={200} value={f.description} onChange={e => setF({ ...f, description: e.target.value })} data-testid="m-badges-description" /></F>
        {problem && <p className="text-sm text-rose-700 dark:text-rose-300" role="alert" data-testid="m-badges-problem">{problem}</p>}
        {b?.system && <p className="pb-1 text-sm text-slate-500">Posted automatically, so it can’t be retired.</p>}
      </div>
    </BottomSheet>
  );
}

export function CelebrationsPage() {
  const ws = useWorkspaceId();
  const q = useWorkspacePrefs();
  const channels = useQuery({ queryKey: ['channels-browse', ws], queryFn: () => api.browseChannels(ws!), enabled: Boolean(ws) });
  // t647u: saves as you go (all choices, so straight away), with Saving… / Saved or the reason.
  const form = useAutosaveForm<CelebrationsForm>(q.data?.engagement, async v => {
    queryClient.setQueryData(prefsKey(ws ?? ''), await api.saveWorkspacePrefs(ws!, 'engagement', v));
  }, { fallback: {} as CelebrationsForm });
  if (!q.data) return <div className="p-6"><Spinner /></div>;
  const f = form.draft;
  const set = (p: Partial<CelebrationsForm>) => form.set(p, true);
  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="m-celebrations">
      <div className="min-h-0 flex-1 overflow-y-auto pb-4">
        <WsHead>Birthdays</WsHead>
        <div className={`${card} space-y-1`}>
          <WsSwitch label="Post birthdays in the kudos feed and a channel" checked={f.birthday_posts} onChange={birthday_posts => set({ birthday_posts })} testId="m-celebrations-birthdays" />
          {f.birthday_posts && <><F label="Birthday channel">
            <Select value={f.birthday_channel_slug} onChange={e => set({ birthday_channel_slug: e.target.value })} data-testid="m-celebrations-channel">
              {birthdayChannels(channels.data?.channels ?? [], f.birthday_channel_slug).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </F>
          <p className="pb-1 text-sm text-slate-500">{BIRTHDAY_CHANNEL_HINT}</p></>}
        </div>
        <WsHead>Sales targets</WsHead>
        <div className={`${card} space-y-1`}>
          <WsSwitch label="Post a kudos when a building hits its sales target" checked={f.sales_target_posts} onChange={sales_target_posts => set({ sales_target_posts })} testId="m-celebrations-sales" />
        </div>
        <p className={note}>{CELEBRATIONS_NOTE}</p>
      </div>
      <div className="flex justify-end px-4 py-2"><SaveStatus s={form.status} testId="m-celebrations-status" /></div>
    </div>
  );
}
